/**
 * صفحه‌ی عمومیِ یک سیگنال — قرینه‌ی apps/mobile/app/s/[token].tsx.
 *
 * این تنها صفحه‌ای است که **بدونِ حساب** باز می‌شود، و روی وب مهم‌ترین صفحه‌ی
 * کلِ محصول است: کسی که لینک را گرفته نه اپ دارد نه حساب.
 *
 * ═══ از مایگریشن 0067: فقط خواندنی ═══
 *
 * تا امروز این صفحه اسم می‌پرسید و جواب می‌گرفت. برداشته شد، چون آن اسم را
 * هیچ‌کس تایید نمی‌کرد: هرکس لینک را داشت می‌توانست هر اسمی بنویسد و هر
 * جوابی بدهد، و آن جواب به هیچ ردیفی از `signal_recipients` نمی‌چسبید.
 * سازنده می‌دید «یکی گفت هستم» بدونِ اینکه بداند کی.
 *
 * حالا مرز صریح است:
 *   /s/<token>   یکی برای همه. فقط می‌گوید چه خبر است.
 *   /i/<token>   مالِ یک نفرِ مشخص. اسمش را می‌داند و جوابش را می‌گیرد.
 *
 * کسی که لینکِ عمومی را گرفته، از راهِ خودش جواب می‌دهد — همان پیامک را جواب
 * می‌دهد یا زنگ می‌زند. که در عمل هم همان کاری‌ست که آدم‌ها می‌کنند.
 *
 * داده از `get_signal_by_token` می‌آید که security definer است: مهمان به
 * خودِ جدول‌ها دسترسی ندارد و فقط همین یک دریچه‌ی باریک برایش باز است.
 *
 * خودِ چیدمان از `GuestSignal` می‌آید — همان بدنه‌ای که `/i/:token` هم
 * دارد. تفاوتِ این دو یک چیز است و این‌جا با نبودِ `onAnswer` گفته می‌شود.
 */
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { publicSignalRepo } from '@/core/data/signalRepo';
import { t, useLocale } from '@/core/lib/i18n';
import { signalUrl } from '@/core/lib/shareLink';
import { trackEventOpen, trackFunnel } from '@/core/lib/analytics';
import { Card } from '@/ui/kit';
import { GuestSignal } from './GuestSignal';

export function PublicSignalPage() {
  useLocale();
  const { token } = useParams<{ token: string }>();

  const signal = useQuery({
    queryKey: ['public', token],
    queryFn: () => publicSignalRepo.byToken(token!),
    enabled: !!token,
  });

  // قیف (WHOSUP-291): ورود از لینکِ عمومی و باز شدنِ رویداد — هر دو یک بار.
  const sid = signal.data?.id;
  useEffect(() => {
    if (!sid) return;
    trackFunnel('link_entry', { kind: 'public', id: sid });
    trackEventOpen(sid, 'signal', 'public_link');
  }, [sid]);

  if (signal.isLoading) return <div className="feed"><p className="dim">…</p></div>;

  // توکنِ بی‌جواب یعنی لینک غلط یا سیگنالِ پاک‌شده. عمداً همون یک جمله برای
  // هر دو: بیشتر از این گفتن، یعنی لو دادنِ اینکه کدوم توکن‌ها وجود دارن.
  // سیگنالِ **لغوشده** دیگه این‌جا نمی‌افته (مایگریشن 0080) — لینکش زنده‌ست
  // و صفحه خودش می‌گه لغو شد.
  if (!signal.data) {
    return <div className="feed"><Card><p>{t('linkExpired')}</p></Card></div>;
  }

  // `pageUrl` یعنی این صفحه هم «به تقویمم اضافه کن» دارد. تا امروز فقط
  // لینکِ شخصی داشتش، بی‌دلیل: کسی که لینکِ عمومی را گرفته هم می‌رود، فقط
  // جوابش را از راهِ دیگری می‌دهد.
  return <GuestSignal source={signal.data} pageUrl={signalUrl(token!)} />;
}
